// Quests as data. The server tracks progress from things it can see happen (foraging, chat, places you walk
// to, parties, what you wear, skill levels) and pays rewards inside the item ledger, exactly once.
// Quests start on their own when the quest before them is done; later, townsfolk will hand them out.
import type { EquipSlot } from './items.js';

/** Named places in the world, for "go to" goals and the journal. Positions in tiles. */
export interface Area { id: string; map: string; name: string; at: [number, number]; radius: number }
export const AREAS: Area[] = [
  { id: 'fountain', map: 'town', name: 'the town fountain', at: [19.5, 13.5], radius: 3.5 },
  { id: 'dock', map: 'town', name: 'the fishing dock', at: [7.5, 20.5], radius: 2 },
  { id: 'farm', map: 'town', name: 'the farm plots', at: [31, 22], radius: 4 },
  { id: 'bakery', map: 'town', name: 'the bakery', at: [6, 9], radius: 2.5 },
  { id: 'florist', map: 'town', name: 'the flower shop', at: [29, 9], radius: 2.5 },
];

export type QuestGoal =
  | { kind: 'say'; count: number; text: string }                          // talk in /say near others
  | { kind: 'visit'; area: string; text: string }
  | { kind: 'forage'; item: string; count: number; text: string }
  | { kind: 'party'; text: string }                                       // be in a party with someone
  | { kind: 'wear'; slots: EquipSlot[]; text: string }                    // something worn in each slot
  | { kind: 'skill'; skill: string; level: number; text: string };

export interface QuestDef {
  id: string; name: string; giver: string;
  story: string;                 // what the giver says, in their voice
  goals: QuestGoal[];
  after?: string;                // starts once this quest is done
  reward: { items?: { def: string; qty: number }[]; gold?: number };
}

export const QUESTS: QuestDef[] = [
  { id: 'welcome', name: 'Welcome to Tiny Town', giver: 'Mayor Pim',
    story: "Oh, a new face! Have a look around. Say hello to someone, toss a wish in the fountain, and see the dock. Come back with a smile.",
    goals: [{ kind: 'say', count: 1, text: 'Say hello to someone nearby' }, { kind: 'visit', area: 'fountain', text: 'Visit the town fountain' }, { kind: 'visit', area: 'dock', text: 'Walk out onto the fishing dock' }],
    reward: { items: [{ def: 'straw_hat', qty: 1 }], gold: 10 } },
  { id: 'mushrooms', name: 'Mushroom Hunt', giver: 'Baker Rosa', after: 'welcome',
    story: "My mushroom tarts won't bake themselves! Bring back five red mushrooms from the shady spots by the trees.",
    goals: [{ kind: 'forage', item: 'mushroom', count: 5, text: 'Forage Red Mushrooms' }],
    reward: { items: [{ def: 'wooden_sword', qty: 1 }, { def: 'honey_loaf', qty: 2 }], gold: 15 } },
  { id: 'dressed', name: 'Dressed for Adventure', giver: 'Mayor Pim', after: 'mushrooms',
    story: "You look like you're going places. Pop on that hat and pick up something to swing. Practice only, please.",
    goals: [{ kind: 'wear', slots: ['head', 'mainhand'], text: 'Wear a hat and hold a weapon' }],
    reward: { items: [{ def: 'blue_scarf', qty: 1 }] } },
  { id: 'together', name: 'Better Together', giver: 'Guildmaster Oak', after: 'welcome',
    story: "Nobody adventures alone in Tiny Town. Find a friend: invite someone, or raise your flag in the Party Finder.",
    goals: [{ kind: 'party', text: 'Join a party' }],
    reward: { items: [{ def: 'potion', qty: 3 }], gold: 20 } },
  { id: 'blossoms', name: 'A Bouquet for the Florist', giver: 'Florist Ivy', after: 'mushrooms',
    story: "The pink blossoms near my shop are lovely this time of year. Three would make a perfect bouquet.",
    goals: [{ kind: 'forage', item: 'flower', count: 3, text: 'Forage Pink Blossoms' }, { kind: 'visit', area: 'florist', text: 'Bring them to the flower shop' }],
    reward: { items: [{ def: 'round_glasses', qty: 1 }], gold: 15 } },
  { id: 'night_lights', name: 'Lights of the Night', giver: 'Lamplighter Tomas', after: 'welcome',
    story: "Evening, friend. When the lamps are lit, moonflowers open all over town. Bring me three and I'll brew you something special.",
    goals: [{ kind: 'forage', item: 'moonflower', count: 3, text: 'Pick Moonflowers (they only open at night)' }],
    reward: { items: [{ def: 'moon_tea', qty: 2 }], gold: 20 } },
  { id: 'after_rain', name: 'After the Rain', giver: 'Gardener Lia', after: 'welcome',
    story: "Do you hear that? Rain! The blue rain caps will be popping up in the shade. Could you gather four before they hide again?",
    goals: [{ kind: 'forage', item: 'rain_cap', count: 4, text: 'Gather Rain Caps (after it rains)' }],
    reward: { items: [{ def: 'potion', qty: 2 }], gold: 20 } },
  { id: 'green_thumb', name: 'Green Thumb', giver: 'Farmer Bo', after: 'blossoms',
    story: "You've got a knack for finding things. Keep at it and you'll be a real forager.",
    goals: [{ kind: 'skill', skill: 'foraging', level: 4, text: 'Reach Foraging level 4' }, { kind: 'visit', area: 'farm', text: 'Visit the farm plots' }],
    reward: { items: [{ def: 'traveler_cloak', qty: 1 }, { def: 'lantern', qty: 1 }], gold: 40 } },
];
export const questDef = (id: string) => QUESTS.find(q => q.id === id);

/** How far along a goal is: [done, needed]. */
export function goalNeed(g: QuestGoal): number { return g.kind === 'say' || g.kind === 'forage' ? g.count : 1; }

/** A quest as the player sees it: progress per goal. */
export interface QuestView { id: string; state: 'active' | 'done'; progress: number[] }
